// D34: one game is one video. Only a game still stored as several whole-game or game-part rows is
// shown as parts; shift and highlight clips are always separate videos, even when they share a game.
const PART_TYPES = ['whole_game', 'game_part']

// A recording of the game itself (one video, or one part of it), as opposed to a shift or highlight clip.
export const isGameVideo = upload => PART_TYPES.includes(upload?.sourceType)

// A failed row whose file never arrived, usually left behind when the upload form retried a file.
export const isDeadUpload = upload => upload?.status === 'failed' && upload.processingErrorCode === 'UPLOAD_INCOMPLETE'

// A video combined from earlier, separately stored parts (release B). It is always one video of its own, even
// next to its parts, which are listed again only while it is failed.
export const isCombinedVideo = upload => Boolean(upload?.combinedFrom?.length)

const isGamePart = upload => Boolean(upload?.gameId) && isGameVideo(upload) && !isDeadUpload(upload) && !isCombinedVideo(upload)

// Lowest part number first, like the API (rows without one first); the sort keeps list order for ties.
const sequence = upload => upload.sequenceIndex ?? -1
const bySequence = (a, b) => sequence(a) - sequence(b)

// How far a row got toward playback; a failed row is last.
const progress = upload => {
  if (upload.readyForPlayback) return 4
  if (upload.status === 'failed') return 0
  if (upload.status === 'pending_upload') return 1
  if (upload.status === 'queued') return 2
  return 3
}
const createdMs = upload => Date.parse(upload.createdAt) || 0
const isFurther = (upload, than) => progress(upload) > progress(than) ||
  (progress(upload) === progress(than) && createdMs(upload) > createdMs(than))

// Real parts always have different part numbers, and the upload form retries a file under the same game and
// number (null for a single video). So each game and part number keeps one row, the one that got furthest: an
// abandoned try stays "Waiting for upload" for 45 minutes before the reconciler marks it dead, and must not make
// one video look like a two-part game meanwhile. A row nothing replaced stays visible, so no upload is lost.
// A combined video shares its first part's game but is never a retry of anything.
const partKey = upload => `${upload.gameId}:${upload.sequenceIndex ?? ''}`
const mayBeRetry = upload => Boolean(upload.gameId) && !isCombinedVideo(upload)
export const withoutReplacedRetries = uploads => {
  const kept = new Map()
  ;(uploads || []).filter(mayBeRetry).forEach(upload => {
    const current = kept.get(partKey(upload))
    if (!current || isFurther(upload, current)) kept.set(partKey(upload), upload)
  })
  return (uploads || []).filter(upload => !mayBeRetry(upload) || kept.get(partKey(upload)) === upload)
}

// The game's parts among these uploads, one per part number. Fewer than two means it is not a multi-part game.
export const gamePartsFor = (uploads, gameId) => gameId
  ? withoutReplacedRetries(uploads).filter(upload => upload.gameId === gameId && isGamePart(upload)).sort(bySequence)
  : []

// A part's number: the stored one, which auto-numbered names also carry, else its place among the parts.
export const partNumber = (part, parts) => part.sequenceIndex || parts.indexOf(part) + 1

// "Part 2 of 3" while the parts are numbered 1 to N. When a part is missing (its upload never arrived), only
// "Part 3", so the label never contradicts the part's name or the Game page.
export const partLabel = (part, parts) => {
  const numbered = parts.every((other, index) => partNumber(other, parts) === index + 1)
  return numbered ? `Part ${partNumber(part, parts)} of ${parts.length}` : `Part ${partNumber(part, parts)}`
}

// The library's entries in list order: one per video, and one per multi-part game where its first row appears.
export const libraryEntries = uploads => {
  const rows = withoutReplacedRetries(uploads)
  const partsByGame = new Map()
  rows.filter(isGamePart).forEach(upload => {
    partsByGame.set(upload.gameId, [...(partsByGame.get(upload.gameId) || []), upload])
  })
  const shown = new Set()
  return rows.flatMap(upload => {
    const parts = isGamePart(upload) ? partsByGame.get(upload.gameId) : null
    if (!parts || parts.length < 2) return [{ upload, parts: null }]
    if (shown.has(upload.gameId)) return []
    shown.add(upload.gameId)
    const ordered = [...parts].sort(bySequence)
    return [{ upload: ordered[0], parts: ordered }]
  })
}

// Furthest from playable first, so a game card never reads "Ready" while a part is not.
const statusRank = upload => {
  if (upload.readyForPlayback) return 0
  if (upload.status === 'failed') return 5
  if (upload.status === 'ready') return 4
  if (upload.status === 'pending_upload') return 3
  if (upload.status === 'queued') return 2
  return 1
}

export const worstStatusPart = parts => (parts || []).reduce(
  (worst, part) => !worst || statusRank(part) > statusRank(worst) ? part : worst,
  null
)

// Auto-numbered parts are named "<game> - Part N"; the game's own name is preferred when the API sends it.
export const gameName = upload => upload?.game?.name || (upload?.name || '').replace(/\s+-\s+Part\s+\d+$/i, '')
